import { useAtomValue } from 'jotai';
import { useCallback, useEffect } from 'react';
import { Animated } from 'react-native';
import { twJoin } from 'tailwind-merge';

import { AnnouncementBannerBody } from '@/components/announcementBanner/AnnouncementBannerBody';
import type {
  AnnouncementBannerProps,
  AnnouncementPresentation,
} from '@/components/announcementBanner/types';
import { TONE_STYLES, parseAlertVariant } from '@/components/announcementBanner/variants';
import { View } from '@/components/ui/view';
import { GLOBAL_BANNER_CARD, GLOBAL_BANNER_CONTENT } from '@/constants/banners';
import { openExternalLink } from '@/hooks/useAnnouncements';
import { useCrossfade } from '@/hooks/useCrossfade';
import { userIdAtom } from '@/services/globalState';
import { useAnnouncementRotationStore } from '@/services/stores/announcementRotationStore';

/**
 * How the banner presents itself in a browser.
 *
 * Web stacks the call to action under the copy below the `sm` breakpoint and
 * returns to a row above it, so the copy gets the full width where it is
 * narrowest. That headroom is what lets web carry a full type scale where
 * native cannot.
 *
 * No `ctaHitSlop`: react-native-web's Pressable silently ignores it, so the box
 * has to be sized instead. The CTA is 36px here, still under the 48px floor in
 * `accessibility.md` — a known gap, left alone because the only fix grows the
 * card past its `min-h-16` and wants a design decision.
 */
const WEB_PRESENTATION: AnnouncementPresentation = {
  alertClassName:
    'flex-col items-start gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-2',
  leadingClassName: 'flex-shrink-0 self-start',
  // See the native shell: `leading-none` crops an emoji, whose ink box is taller
  // than the em box its line height would be pinned to. The flex centring stays,
  // which is what react-native-web needs to sit the glyph on the text's center.
  emojiClassName: 'text-lg flex items-center',
  badgeClassName:
    '-mt-0.5 h-6 shrink-0 items-center justify-center self-start rounded-md bg-primary-100 px-2',
  titleSize: 'md',
  messageSize: 'sm',
  ctaSize: 'sm',
  ctaClassName: 'shrink-0 w-full sm:w-auto',
};

/**
 * The announcement banner in a browser, framed as an inset card.
 *
 * The card is what lets this stack with the Fellows promo and still read as two
 * surfaces — see `banners.md`. Native has no equivalent: there is no app-level
 * chrome above a screen to stack into, so it renders full-bleed in the header
 * and no promo tier exists there at all.
 *
 * Hovering or focusing the card holds the rotation where it is. The card has no
 * dismissal and never scrolls away, so there is nothing else here a reader can use to
 * stop content changing under them — which is what the pause is for, rather than
 * polish. It is the whole of this card's answer to auto-updating content.
 */
export function AnnouncementBanner({
  isEnabled = false,
  contentKey = null,
  ...props
}: AnnouncementBannerProps) {
  const userId = useAtomValue(userIdAtom);

  const setPaused = useAnnouncementRotationStore((state) => state.setPaused);

  const { onPress, ...content } = props;

  const { opacity, displayed, isSwapping } = useCrossfade(contentKey, content);

  const pause = useCallback(() => setPaused(true), [setPaused]);

  const resume = useCallback(() => setPaused(false), [setPaused]);

  /**
   * react-native-web forwards mouse events on a `View` — they are in its
   * forwarded prop list — but React Native's own `ViewProps` cannot declare
   * them, because the platforms it types have no pointer to enter with. Spread
   * rather than passed directly: a JSX spread is not excess-property checked, so
   * this states the gap once instead of casting the whole element.
   *
   * Focus is not in the same position. `onFocus` and `onBlur` are typed on
   * `ViewProps`, and React's versions bubble, so a keyboard user reaching the
   * call to action inside the card pauses it just as a pointer over the card
   * does.
   */
  const hoverProps: { onMouseEnter: () => void; onMouseLeave: () => void } = {
    onMouseEnter: pause,
    onMouseLeave: resume,
  };

  /**
   * A card that unmounts while the pointer is still over it never sees the
   * matching leave event, which would strand the rotation paused for the rest of
   * the session.
   */
  useEffect(() => () => setPaused(false), [setPaused]);

  if (!isEnabled || !userId) {
    return null;
  }

  const handlePress = () => {
    if (onPress) {
      onPress();

      return;
    }

    if ((displayed.ctaAction === 'navigate' || !displayed.ctaAction) && displayed.ctaUrl) {
      openExternalLink(displayed.ctaUrl);
    }
  };

  const { tone } = parseAlertVariant(displayed.variant ?? 'success');

  return (
    <View
      className={twJoin(GLOBAL_BANNER_CARD, TONE_STYLES[tone].background)}
      {...hoverProps}
      onFocus={pause}
      onBlur={resume}>
      <Animated.View style={{ opacity }} pointerEvents={isSwapping ? 'none' : 'auto'}>
        <View className={GLOBAL_BANNER_CONTENT}>
          <AnnouncementBannerBody
            {...displayed}
            presentation={WEB_PRESENTATION}
            onCtaPress={handlePress}
          />
        </View>
      </Animated.View>
    </View>
  );
}
